import * as React from 'react';
import { useEffect } from 'react';
import ReactModal from 'react-modal';
import type { Props as ReactModalProps } from 'react-modal';

import classNames from 'classnames';

import { StyleSheet, css } from '@coursera/coursera-ui';

import CloseButton from 'bundles/authentication/shared/components/authentication-modal/CloseButton';
import Header from 'bundles/authentication/shared/components/authentication-modal/Header';

import _t from 'i18n!nls/authentication';

type PropsFromCaller = {
  preventModalClose?: boolean;
} & ReactModalProps;

type PropsToComponent = { children: React.ReactNode } & PropsFromCaller;

const styles = StyleSheet.create({
  modalRoot: {
    backgroundColor: 'var(--cds-color-white-0)',
    maxHeight: '100%',
    margin: '0 auto',
    position: 'relative',
    overflow: 'auto',
    padding: '24px 16px',
    width: '492px',
    borderRadius: '6px',
    display: 'inline-block',
    '@media (max-width: 600px)': {
      width: '100%',
      height: '100%',
      padding: '48px 0px 0px',
    },
    '@media (min-width: 1440px)': {
      padding: '16px 24px',
    },
  },
  closeBtn: {
    borderRadius: '50%',
    position: 'fixed',
    right: '3px',
    top: '3px',
    zIndex: 10,
    '@media (min-width: 600px)': {
      position: 'absolute',
    },
  },
  portal: {
    left: '0',
    height: '100%',
    position: 'fixed',
    top: '0',
    width: '100%',
    zIndex: '10000',
  },
  bodyOpen: {
    overflow: 'hidden',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.83) !important',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    '@media (min-width: 600px)': {
      padding: '12px 0',
    },
  },
});

const Modal = ({
  children,
  onRequestClose,
  preventModalClose = false,
  overlayClassName,
  portalClassName,
  bodyOpenClassName,
  contentLabel = _t('Auth Modal'),
  ...props
}: PropsToComponent) => {
  useEffect(() => {
    // #rendered-content is the default mount node for all coursera apps
    ReactModal.setAppElement('#rendered-content');
  }, []);

  const bodyOpenClassnames = classNames(css(styles.bodyOpen).className, bodyOpenClassName);
  const overlayClassnames = classNames(css(styles.overlay).className, overlayClassName);
  const portalClassnames = classNames(css(styles.portal).className, portalClassName);

  return (
    <div>
      <ReactModal
        {...props}
        {...css(styles.modalRoot)}
        bodyOpenClassName={bodyOpenClassnames}
        closeTimeoutMS={0}
        contentLabel={contentLabel}
        onRequestClose={onRequestClose}
        overlayClassName={overlayClassnames}
        portalClassName={portalClassnames}
      >
        <Header />

        {!preventModalClose && <CloseButton {...css(styles.closeBtn)} onClick={onRequestClose} />}
        {children}
      </ReactModal>
    </div>
  );
};

export default Modal;
